import loadComponents from './components';
import loadBlocks from './blocks';
import en from './locale/en';

const PLUGIN_ID = 'grapesjs-plugin-thumbai';

const getDefaultDomain = () => {
  if (typeof window !== 'undefined' && window.location) {
    return window.location.hostname;
  }

  return 'example.com';
};

const getLocaleValue = (locale = {}, path, fallback = '') => {
  if (!path) return fallback;
  const segments = path.split('.');
  let current = locale;
  for (const segment of segments) {
    if (!current || typeof current !== 'object' || !(segment in current)) {
      return fallback;
    }
    current = current[segment];
  }
  return current;
};

const buildDefaultLabels = (locale = {}) => {
  const toolbar = locale.toolbar || {};
  const block = locale.block || {};
  const labels = locale.labels || {};

  return {
    section: labels.section || '',
    prompt: labels.prompt || '',
    paramsHeading: labels.paramsHeading || '',
    button: labels.button || '',
    generateAction: labels.generateAction || labels.button || '',
    traitButton: labels.traitButton || labels.generateAction || labels.button || '',
    save: labels.save || '',
    saved: labels.saved || '',
    serviceLoaded: labels.serviceLoaded || '',
    modalTitle: labels.modalTitle || '',
    modalHeading: labels.modalHeading || labels.modalTitle || '',
    modalSubheading: labels.modalSubheading || '',
    service: labels.service || '',
    servicePlaceholder: labels.servicePlaceholder || '',
    selectService: labels.selectService || '',
    modalLoading: labels.modalLoading || '',
    loading: labels.loading || '',
    success: labels.success || '',
    error: labels.error || '',
    missingCredentials: labels.missingCredentials || '',
    missingPromptId: labels.missingPromptId || '',
    missingParams: labels.missingParams || '',
    missingService: labels.missingService || '',
    missingImage: labels.missingImage || '',
    base64ConversionFailed: labels.base64ConversionFailed || '',
    uploadError: labels.uploadError || '',
    noParams: labels.noParams || '',
    loadServiceError: labels.loadServiceError || '',
    serviceInfoError: labels.serviceInfoError || '',
    traitSuccess: labels.traitSuccess || labels.success || '',
    traitError: labels.traitError || labels.error || '',
    toolbarLabel: toolbar.label || '',
    toolbarTitle: labels.toolbarTitle || toolbar.title || toolbar.label || '',
    blockLabel: block.label || '',
    blockCategory: block.category || '',
    blockPlaceholderText: block.placeholderText || '',
    paramDefaultLabel: labels.paramsDefaultLabel || '',
  };
};

const buildServices = (locale = {}, customServices) => {
  if (Array.isArray(customServices) && customServices.length) {
    return customServices;
  }

  const serviceMap = locale.services || {};
  return Object.keys(serviceMap).map((slug) => ({
    slug,
    label: serviceMap[slug],
  }));
};

const buildParams = (locale = {}, customParams) => {
  if (Array.isArray(customParams) && customParams.length) {
    return customParams;
  }

  const entries = (locale.params && locale.params.entries) || {};
  return Object.keys(entries).map((id) => ({
    id,
    label: entries[id].label || '',
    placeholder: entries[id].placeholder || '',
    required: entries[id].required !== false,
  }));
};

const buildBlockOptions = (locale = {}, customBlock = {}, labels = {}) => {
  const defaultBlock = locale.block || {};
  const baseBlock = {
    label: labels.blockLabel || defaultBlock.label || labels.section || '',
    category: labels.blockCategory || defaultBlock.category || '',
    placeholderText: labels.blockPlaceholderText || defaultBlock.placeholderText || '',
    placeholderSrcTemplate: defaultBlock.placeholderSrcTemplate || '',
    media: defaultBlock.media || '',
    attributes: {},
  };

  const merged = {
    ...baseBlock,
    ...(customBlock || {}),
    attributes: {
      ...baseBlock.attributes,
      ...(customBlock && customBlock.attributes ? customBlock.attributes : {}),
    },
  };

  const textForPlaceholder = merged.placeholderText || merged.label;
  if (!merged.placeholderSrc) {
    if (merged.placeholderSrcTemplate && merged.placeholderSrcTemplate.includes('{text}')) {
      merged.placeholderSrc = merged.placeholderSrcTemplate.replace(
        '{text}',
        encodeURIComponent(textForPlaceholder || '')
      );
    } else {
      merged.placeholderSrc = `https://placehold.co/640x360/111/fff?text=${encodeURIComponent(textForPlaceholder || '')}`;
    }
  }

  return merged;
};

export default (editor, opts = {}) => {
  const locale = en[PLUGIN_ID] || {};
  const baseOptions = {
    i18n: {},
    apiBase: 'https://thumb.onl/api/service',
    apiToken: '',
    userId: '',
    serviceSlug: '',
    onImageResponse: null,
    uploadImage: null,
    base64Image: false,
    clientDomain: getDefaultDomain(),
    promptPlaceholder: '',
    autoPrefillDemoRequest: true,
    disableThumbBlock: false,
    services: [],
    params: [],
    block: {},
    labels: {},
  };

  const options = {
    ...baseOptions,
    ...opts,
  };

  const defaultLabels = buildDefaultLabels(locale);
  options.labels = {
    ...defaultLabels,
    ...(opts.labels || {}),
  };

  options.services = buildServices(locale, opts.services);
  options.params = buildParams(locale, opts.params);

  const promptPlaceholder = typeof opts.promptPlaceholder === 'string'
    ? opts.promptPlaceholder
    : getLocaleValue(locale, 'prompts.placeholder', '');
  options.promptPlaceholder = promptPlaceholder;

  options.block = buildBlockOptions(locale, opts.block, options.labels);

  if (!options.block.attributes || !options.block.attributes.src) {
    options.block.attributes = {
      ...options.block.attributes,
      src: options.block.placeholderSrc,
    };
  }

  options.base64Image = Boolean(options.base64Image || options.base64_image);
  options.uploadImage = options.uploadImage || options.upload_image || null;

  // Validate required credentials. If missing, surface an alert and skip plugin setup
  // so the editor itself can still initialize without ThumbAI functionality.
  if (!options.apiToken || !options.userId) {
    const msg = options.labels.missingCredentials || 'ThumbAI plugin: apiToken and/or userId is missing. Please update the plugin options.';
    if (typeof window !== 'undefined' && window.alert) {
      try {
        window.alert(msg);
      } catch (e) {
        // ignore alert failures in non-browser environments
      }
    }
    // Log details for debugging (but avoid printing tokens in production)
    try {
      console.warn('ThumbAI plugin not initialized: missing credentials', {
        apiToken: options.apiToken ? '[REDACTED]' : options.apiToken,
        userId: options.userId,
      });
    } catch (e) {
      // noop
    }

    return;
  }

  loadComponents(editor, options);
  loadBlocks(editor, options);

  if (editor.I18n) {
    editor.I18n.addMessages({
      en,
      ...options.i18n,
    });
  }
};